import React from 'react';
import styled, { css } from 'styled-components';
import Loader from '../../Loader/Loader';

const primaryCSS = css`
  background: ${({ theme }) => theme.colors.gradient};
  color: ${({ theme }) => theme.colors.btnWhite};
  &:hover {
    background: ${({ theme }) => theme.colors.hoverGradient};
  }
`;

const secondaryCSS = css`
  color: ${({ theme }) => theme.colors.gradient};
  background: ${({ theme }) => theme.colors.gradient};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.gp2};
  padding: calc(0.5rem - 1px) 2rem;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverGradient};
    color: ${({ theme }) => theme.colors.white};
    background-clip: inherit;
    -webkit-background-clip: inherit;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.btnWhite};
  }

  @supports not (-webkit-background-clip: text) {
    background-clip: none;
  }
`;

const disabledCSS = css`
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.cflowerBlue};
  cursor: default;
`;
const disabledSecondaryCSS = css`
  color: ${({ theme }) => theme.colors.cflowerBlue};
  background: transparent;
  background-clip: inherit;
  -webkit-text-fill-color: ${({ theme }) => theme.colors.cflowerBlue};
  border: 1px solid ${({ theme }) => theme.colors.cflowerBlue};
  -webkit-background-clip: inherit;
  cursor: default;
`;

interface StyledButtonProps {
  primary?: boolean;
  secondary?: boolean;
  $constWidth?: number;
  $small?: boolean;
}

const StyledButton = styled.button.attrs<{ primary?: boolean; secondary?: boolean }>((props) => ({
  type: 'button',
  ...props,
}))<StyledButtonProps>`
  font-family: Poppins, sans-serif;
  display: inline-block;
  appearance: none;
  border: none;
  padding: 1px 1.8rem;
  outline: none;
  cursor: pointer;
  white-space: nowrap;
  margin: 0;
  text-decoration: none;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  text-transform: uppercase;
  height: 52px;
  min-width: 150px;
  ${({ primary }) => primary && primaryCSS};
  ${({ secondary }) => secondary && secondaryCSS};
  &:disabled {
    ${({ secondary }) => (secondary ? disabledSecondaryCSS : disabledCSS)};

    .loader {
      --_g: no-repeat
        radial-gradient(
          farthest-side,
          ${({ theme, secondary }) => (secondary ? theme.colors.cflowerBlue : theme.colors.white)} 90%,
          #0000
        );
    }
  }
  border-radius: 3px;

  ${({ $small }) =>
    $small &&
    css`
      height: 36px;
      min-width: 100px;
      padding: 1px 1.2rem;
      font-size: 13px;
      line-height: 20px;
    `}

  ${({ $constWidth }) =>
    $constWidth &&
    css`
      width: ${$constWidth}px;
      ${({ theme }) => theme.breakpoints.down('md')} {
        width: ${$constWidth * 0.8}px;
      }
      ${({ theme }) => theme.breakpoints.down('sm')} {
        width: ${$constWidth * 0.6}px;
      }
    `}
  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 40px;
    min-width: 120px;
    padding: 1px 1.3rem;
    font-size: 12px;
    svg {
      transform: scale(0.8);
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 34px;
    min-width: 100px;
    padding: 1px 0.8rem;
    font-size: 10px;
    svg {
      transform: scale(0.6);
    }
  }
`;

interface ButtonProps extends StyledButtonProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
}

const Button: React.FC<ButtonProps> = ({ loading, children, disabled, ...rest }) => {
  return (
    <StyledButton {...rest} disabled={loading || disabled}>
      {loading ? <Loader size="small" /> : children}
    </StyledButton>
  );
};

export default Button;
